import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';

const SiteSettings = ({ config, onUpdateConfig, isLoading }) => {
  const [formData, setFormData] = useState({
    siteName: '',
    domain: '',
    currency: 'EUR',
    prebidTimeout: 1000,
    debugMode: false,
    priceGranularity: 'medium',
    customPriceGranularity: {
      buckets: [
        { precision: 2, min: 0, max: 3, increment: 0.01 },
        { precision: 2, min: 3, max: 8, increment: 0.05 },
        { precision: 2, min: 8, max: 20, increment: 0.5 }
      ]
    },
    contextDefinitions: {
      1: 'article',
      2: 'homepage', 
      3: 'category'
    },
    analytics: {
      enabled: true,
      bidderEvents: false,
      trackerEvents: true,
      cxense: {
        enabled: false,
        siteId: '',
        contextualQueryId: '',
        userSegmentQueryId: '',
        customParameters: {}
      }
    }
  });
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  // Update form data when config changes
  useEffect(() => {
    if (config && config.site) {
      setFormData({
        siteName: config.site.siteName || '',
        domain: config.site.domain || '',
        currency: config.site.currency || 'EUR',
        prebidTimeout: config.site.prebidTimeout || 1000,
        debugMode: config.site.debugMode || false,
        priceGranularity: config.site.priceGranularity || 'medium',
        customPriceGranularity: config.site.customPriceGranularity || {
          buckets: [
            { precision: 2, min: 0, max: 3, increment: 0.01 },
            { precision: 2, min: 3, max: 8, increment: 0.05 },
            { precision: 2, min: 8, max: 20, increment: 0.5 }
          ]
        },
        contextDefinitions: config.site.contextDefinitions || {
          1: 'article',
          2: 'homepage', 
          3: 'category'
        },
        analytics: config.site.analytics || {
          enabled: true,
          bidderEvents: false,
          trackerEvents: true,
          cxense: {
            enabled: false,
            siteId: '',
            contextualQueryId: '',
            userSegmentQueryId: '',
            customParameters: {}
          }
        }
      });
    }
  }, [config]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAnalyticsChange = (provider, field, value) => {
    setFormData(prev => {
      if (provider === '') {
        // Handle top-level analytics fields
        return {
          ...prev,
          analytics: {
            ...prev.analytics,
            [field]: value
          }
        };
      } else {
        // Handle provider-specific fields
        return {
          ...prev,
          analytics: {
            ...prev.analytics,
            [provider]: {
              ...prev.analytics[provider],
              [field]: value
            }
          }
        };
      }
    });
    
    // Clear analytics-related errors
    const errorKey = provider === '' ? `analytics.${field}` : `analytics.${provider}.${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.siteName.trim()) {
      newErrors.siteName = 'Site name is required';
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.domain)) {
      newErrors.domain = 'Please enter a valid domain (e.g., example.com)';
    }

    if (!formData.currency.trim()) {
      newErrors.currency = 'Currency is required';
    }

    if (!formData.prebidTimeout || formData.prebidTimeout < 500 || formData.prebidTimeout > 5000) {
      newErrors.prebidTimeout = 'Prebid timeout must be between 500ms and 5000ms';
    }

    // Validate custom price granularity buckets
    if (formData.priceGranularity === 'custom') {
      if (!formData.customPriceGranularity.buckets || formData.customPriceGranularity.buckets.length === 0) {
        newErrors.customPriceGranularity = 'At least one price bucket is required for custom granularity';
      } else {
        // Check for overlapping buckets
        const buckets = formData.customPriceGranularity.buckets;
        for (let i = 0; i < buckets.length; i++) {
          if (buckets[i].min >= buckets[i].max) {
            newErrors.customPriceGranularity = `Bucket ${i + 1}: Min must be less than Max`;
            break;
          }
          if (buckets[i].increment <= 0) {
            newErrors.customPriceGranularity = `Bucket ${i + 1}: Increment must be greater than 0`;
            break;
          }
          // Check for overlaps with other buckets
          for (let j = i + 1; j < buckets.length; j++) {
            if (buckets[i].max > buckets[j].min) {
              newErrors.customPriceGranularity = `Buckets ${i + 1} and ${j + 1} overlap`;
              break;
            }
          }
        }
      }
    }

    // Validate cXense targeting settings
    if (formData.analytics?.cxense?.enabled) {
      if (!formData.analytics.cxense.contextualQueryId?.trim()) {
        newErrors['analytics.cxense.contextualQueryId'] = 'Contextual Query ID is required when enabled';
      }
      if (!formData.analytics.cxense.userSegmentQueryId?.trim()) {
        newErrors['analytics.cxense.userSegmentQueryId'] = 'User Segment Query ID is required when enabled';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      // Update the site section of the config
      const updatedConfig = {
        ...config,
        site: {
          ...config.site,
          ...formData
        }
      };

      await onUpdateConfig(updatedConfig);
      setMessage({ type: 'success', text: 'Site settings updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update site settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (config && config.site) {
      setFormData({
        siteName: config.site.siteName || '',
        domain: config.site.domain || '',
        currency: config.site.currency || 'EUR',
        prebidTimeout: config.site.prebidTimeout || 1000,
        debugMode: config.site.debugMode || false,
        priceGranularity: config.site.priceGranularity || 'medium',
        customPriceGranularity: config.site.customPriceGranularity || {
          buckets: [
            { precision: 2, min: 0, max: 3, increment: 0.01 },
            { precision: 2, min: 3, max: 8, increment: 0.05 },
            { precision: 2, min: 8, max: 20, increment: 0.5 }
          ]
        },
        contextDefinitions: config.site.contextDefinitions || {
          1: 'article',
          2: 'homepage', 
          3: 'category'
        },
        analytics: config.site.analytics || {
          enabled: true,
          bidderEvents: false,
          trackerEvents: true,
          cxense: {
            enabled: false,
            siteId: '',
            contextualQueryId: '',
            userSegmentQueryId: '',
            customParameters: {}
          }
        }
      });
    }
    setErrors({});
    setMessage(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="animate-spin h-6 w-6 text-blue-500" />
        <span className="ml-2 text-gray-600">Loading site settings...</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Site Settings</h2>
        
        {message && (
          <div className={`mb-4 p-4 rounded-md flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            {message.text}
          </div>
        )}

        <div className="space-y-6">
          {/* Site Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site Name
            </label>
            <input
              type="text"
              value={formData.siteName}
              onChange={(e) => handleInputChange('siteName', e.target.value)}
              placeholder="Enter site name"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.siteName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.siteName && (
              <p className="mt-1 text-sm text-red-600">{errors.siteName}</p>
            )}
          </div>

          {/* Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => handleInputChange('domain', e.target.value)}
              placeholder="Enter site domain (e.g., example.com)"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.domain ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <p className="mt-1 text-sm text-gray-500">
              The primary domain for your site (without protocol)
            </p>
            {errors.domain && (
              <p className="mt-1 text-sm text-red-600">{errors.domain}</p>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.currency ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="EUR">EUR - Euro</option>
              <option value="USD">USD - US Dollar</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="JPY">JPY - Japanese Yen</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="AUD">AUD - Australian Dollar</option>
            </select>
            {errors.currency && (
              <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
            )}
          </div>

          {/* Prebid Timeout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prebid Timeout (ms)
            </label>
            <input
              type="number"
              min="500"
              max="5000"
              step="100"
              value={formData.prebidTimeout}
              onChange={(e) => handleInputChange('prebidTimeout', parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.prebidTimeout ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <p className="mt-1 text-sm text-gray-500">
              How long to wait for bids before calling ad server (500-5000ms)
            </p>
            {errors.prebidTimeout && (
              <p className="mt-1 text-sm text-red-600">{errors.prebidTimeout}</p>
            )}
          </div>

          {/* Price Granularity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price Granularity
            </label>
            <select
              value={formData.priceGranularity}
              onChange={(e) => handleInputChange('priceGranularity', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="auto">Auto</option>
              <option value="dense">Dense</option>
              <option value="custom">Custom</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Controls the price bucket granularity for bid values
            </p>
          </div>

          {/* Custom Price Granularity Buckets */}
          {formData.priceGranularity === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Price Buckets
              </label>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bucket
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Precision
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Min
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Max
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Increment
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.customPriceGranularity.buckets.map((bucket, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                          #{index + 1}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            max="5"
                            value={bucket.precision}
                            onChange={(e) => {
                              const newBuckets = [...formData.customPriceGranularity.buckets];
                              newBuckets[index].precision = parseInt(e.target.value) || 0;
                              handleInputChange('customPriceGranularity', {
                                ...formData.customPriceGranularity,
                                buckets: newBuckets
                              });
                            }}
                            className="w-16 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={bucket.min}
                            onChange={(e) => {
                              const newBuckets = [...formData.customPriceGranularity.buckets];
                              newBuckets[index].min = parseFloat(e.target.value) || 0;
                              handleInputChange('customPriceGranularity', {
                                ...formData.customPriceGranularity,
                                buckets: newBuckets
                              });
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={bucket.max}
                            onChange={(e) => {
                              const newBuckets = [...formData.customPriceGranularity.buckets];
                              newBuckets[index].max = parseFloat(e.target.value) || 0;
                              handleInputChange('customPriceGranularity', {
                                ...formData.customPriceGranularity,
                                buckets: newBuckets
                              });
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={bucket.increment}
                            onChange={(e) => {
                              const newBuckets = [...formData.customPriceGranularity.buckets];
                              newBuckets[index].increment = parseFloat(e.target.value) || 0.01;
                              handleInputChange('customPriceGranularity', {
                                ...formData.customPriceGranularity,
                                buckets: newBuckets
                              });
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm">
                          <button
                            type="button"
                            onClick={() => {
                              const newBuckets = formData.customPriceGranularity.buckets.filter((_, i) => i !== index);
                              handleInputChange('customPriceGranularity', {
                                ...formData.customPriceGranularity,
                                buckets: newBuckets
                              });
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={() => {
                  const lastBucket = formData.customPriceGranularity.buckets[formData.customPriceGranularity.buckets.length - 1];
                  const newBucket = {
                    precision: 2,
                    min: lastBucket ? lastBucket.max : 0,
                    max: lastBucket ? lastBucket.max + 10 : 10,
                    increment: 1
                  };
                  handleInputChange('customPriceGranularity', {
                    ...formData.customPriceGranularity,
                    buckets: [...formData.customPriceGranularity.buckets, newBucket]
                  });
                }}
                className="mt-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                + Add Bucket
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Define custom price buckets for granular bid pricing. Buckets should not overlap.
              </p>
              {errors.customPriceGranularity && (
                <p className="mt-1 text-sm text-red-600">{errors.customPriceGranularity}</p>
              )}
            </div>
          )}

          {/* Debug Mode */}
          <div>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.debugMode}
                onChange={(e) => handleInputChange('debugMode', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">
                Debug Mode
              </span>
            </label>
            <p className="mt-1 text-sm text-gray-500 ml-7">
              Enable debug logging for troubleshooting (disable in production)
            </p>
          </div>

          {/* Analytics Configuration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Analytics Configuration
            </label>
            <div className="space-y-4 pl-4 border-l-2 border-gray-100">
              {/* Master Analytics Toggle */}
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.analytics?.enabled || false}
                  onChange={(e) => handleAnalyticsChange('', 'enabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Analytics Tracking
                </span>
              </label>
              <p className="text-sm text-gray-500 ml-7">
                Master control for all analytics events (initialization funnel, retry monitoring, etc.)
              </p>

              {formData.analytics?.enabled && (
                <div className="space-y-4 ml-7">
                  {/* Tracker Events */}
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.analytics?.trackerEvents || false}
                      onChange={(e) => handleAnalyticsChange('', 'trackerEvents', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Tracker Events
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 ml-7">
                    Track initialization funnel, script loading, retry mechanisms (essential for monitoring)
                  </p>

                  {/* Bidder Events */}
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.analytics?.bidderEvents || false}
                      onChange={(e) => handleAnalyticsChange('', 'bidderEvents', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Bidder Events
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 ml-7">
                    Track bid responses, auction wins, timeouts (high resource consumption - disable to reduce 85-90% of events)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* cXense Targeting */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              cXense Targeting
            </label>
            <div className="space-y-4 pl-4 border-l-2 border-gray-100">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.analytics?.cxense?.enabled || false}
                  onChange={(e) => handleAnalyticsChange('cxense', 'enabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable cXense Targeting
                </span>
              </label>
              <p className="text-sm text-gray-500 ml-7">
                Provides user and contextual segments for improved ad targeting
              </p>
              
              {formData.analytics?.cxense?.enabled && (
                <div className="space-y-4 ml-7">
                  {/* Contextual Query ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contextual Query ID
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 6b6f4662590119a61f9f536d73f821b5807c74fe"
                      value={formData.analytics?.cxense?.contextualQueryId || ''}
                      onChange={(e) => handleAnalyticsChange('cxense', 'contextualQueryId', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors['analytics.cxense.contextualQueryId'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Query ID for contextual segments based on page content
                    </p>
                    {errors['analytics.cxense.contextualQueryId'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['analytics.cxense.contextualQueryId']}</p>
                    )}
                  </div>

                  {/* User Segment Query ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      User Segment Query ID
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., ff86ec5c353f42f9a0310e4b35c54883f4a98375"
                      value={formData.analytics?.cxense?.userSegmentQueryId || ''}
                      onChange={(e) => handleAnalyticsChange('cxense', 'userSegmentQueryId', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors['analytics.cxense.userSegmentQueryId'] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Query ID for user behavior segments
                    </p>
                    {errors['analytics.cxense.userSegmentQueryId'] && (
                      <p className="mt-1 text-sm text-red-600">{errors['analytics.cxense.userSegmentQueryId']}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Context Definitions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Context Definitions
            </label>
            <div className="space-y-2">
              {Object.entries(formData.contextDefinitions).map(([id, name]) => (
                <div key={id} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600 w-8">ID {id}:</span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleInputChange('contextDefinitions', {
                      ...formData.contextDefinitions,
                      [id]: e.target.value
                    })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Context name"
                  />
                </div>
              ))}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Define context types using numeric IDs (1=article, 2=homepage, etc.)
            </p>
          </div>

          {/* Read-only Site ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Site ID
            </label>
            <input
              type="text"
              value={config?.site?.siteId || ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="mt-1 text-sm text-gray-500">
              Site identifier (read-only)
            </p>
          </div>

        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="animate-spin h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteSettings;